import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { GeoReportResponse } from '../../../../shared/models/geo-report/GeoReportResponse';
import { A4_WIDTH, A4_HEIGHT, MARGIN_X } from '../../../../shared/services/pdf';
import { PDF_FONTS } from '../../../../shared/services/pdf/pdfTypography';
import chatbeatLogoFull from '../../../../shared/services/pdf/assets/chatbeat-logo-full';
import chatbeatPdfBg from '../../../../shared/services/pdf/assets/chatbeat-pdf-bg';
import { registerPdfFonts } from '../../../../shared/services/pdf/fonts/registerFonts';

registerPdfFonts();

// ─── Design tokens ──────────────────────────────────────────────────────────
const COLORS = {
  primary: '#4f6bed',
  primaryLight: '#eef1fd',
  success: '#16a34a',
  successLight: '#ecfdf5',
  successDark: '#15803d',
  warning: '#d97706',
  warningLight: '#fffbeb',
  warningDark: '#b45309',
  error: '#ef4444',
  errorLight: '#fef2f2',
  errorDark: '#dc2626',
  grey800: '#1e293b',
  grey600: '#475569',
  grey500: '#64748b',
  grey400: '#94a3b8',
  grey200: '#e2e8f0',
  grey100: '#f1f5f9',
  grey50: '#f8fafc',
  white: '#ffffff',
  cardBorder: '#e2e8f0',
};

const styles = StyleSheet.create({
  // ─── Cover / Score ───────────────────────────────────────────────────
  coverBlock: {
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.cardBorder,
    borderRadius: 10,
    padding: 36,
    marginBottom: 20,
    alignItems: 'center',
  },
  coverTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: COLORS.grey800,
    marginBottom: 8,
  },
  coverUrl: {
    fontSize: 14,
    color: COLORS.grey600,
    marginBottom: 20,
    textAlign: 'center',
  },
  coverDate: {
    fontSize: 8.5,
    color: COLORS.grey400,
    marginBottom: 16,
  },
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 10,
  },
  scoreValue: {
    fontSize: 56,
    fontWeight: 'bold',
    lineHeight: 1,
  },
  scoreMax: {
    fontSize: 18,
    color: COLORS.grey400,
    marginBottom: 6,
    marginLeft: 2,
  },
  statusPill: {
    paddingHorizontal: 14,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusPillText: {
    fontSize: 9,
    fontWeight: 'bold',
  },

  // ─── Cover page ──────────────────────────────────────────────────────
  coverPageStyle: {
    width: A4_WIDTH,
    height: A4_HEIGHT,
    fontFamily: 'Inter',
    fontSize: PDF_FONTS.body,
    color: '#000000',
    paddingTop: 0,
    paddingBottom: 60,
    paddingLeft: MARGIN_X,
    paddingRight: MARGIN_X,
  },
  coverBgWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: A4_WIDTH,
    height: A4_HEIGHT,
  },
  coverBgImage: {
    width: '100%',
    height: '100%',
  },
  coverContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  coverLogo: {
    width: 220,
    height: (220 * 129) / 671,
    marginBottom: 32,
  },
  coverFooter: {
    position: 'absolute',
    bottom: 28,
    right: MARGIN_X,
    fontSize: PDF_FONTS.body,
    color: '#000000',
  },

  // ─── Content pages (white bg, small logo) ───────────────────────────
  contentPage: {
    width: A4_WIDTH,
    height: A4_HEIGHT,
    fontFamily: 'Inter',
    fontSize: PDF_FONTS.body,
    color: '#000000',
    paddingTop: 64,
    paddingBottom: 60,
    paddingLeft: MARGIN_X,
    paddingRight: MARGIN_X,
    backgroundColor: COLORS.white,
  },
  contentHeader: {
    position: 'absolute',
    top: 24,
    left: MARGIN_X,
  },
  contentHeaderLogo: {
    width: 80,
    height: (80 * 129) / 671,
  },
  contentFooter: {
    position: 'absolute',
    bottom: 28,
    right: MARGIN_X,
    fontSize: PDF_FONTS.body,
    color: '#000000',
  },

  // ─── Section (no card border on content pages) ─────────────────────
  section: {
    marginBottom: 18,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: COLORS.grey800,
    marginBottom: 10,
    paddingBottom: 6,
    borderBottomWidth: 1.5,
    borderBottomColor: COLORS.primaryLight,
  },
  subheading: {
    fontSize: 11,
    fontWeight: 'bold',
    color: COLORS.grey800,
    marginTop: 14,
    marginBottom: 6,
  },
  bodyText: {
    fontSize: PDF_FONTS.body,
    color: COLORS.grey800,
    lineHeight: 1.7,
    marginBottom: 5,
  },

  // ─── Bullets ─────────────────────────────────────────────────────────
  bulletContainer: {
    marginBottom: 8,
  },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  bulletDot: {
    width: 12,
    fontSize: PDF_FONTS.body,
    lineHeight: 1.7,
  },
  bulletNumbered: {
    width: 20,
    fontSize: PDF_FONTS.body,
    fontWeight: 'bold',
    lineHeight: 1.7,
  },
  bulletText: {
    fontSize: PDF_FONTS.body,
    color: COLORS.grey800,
    flex: 1,
    lineHeight: 1.7,
  },

  // ─── Tables ──────────────────────────────────────────────────────────
  tableContainer: {
    marginBottom: 10,
    marginTop: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: COLORS.cardBorder,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: COLORS.grey50,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.cardBorder,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: COLORS.grey100,
  },
  tableRowLast: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tableRowEven: {
    backgroundColor: COLORS.grey50,
  },
  tableHeaderCell: {
    // flexBasis:0 (not `flex:1`, which leaves basis:auto) so every column is an equal share
    // of the row width regardless of content length — otherwise a long cell (e.g. the §2A
    // "Details" column) shrinks the others to near-zero and text wraps one char per line.
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    fontSize: 8,
    fontWeight: 'bold',
    color: COLORS.grey500,
    padding: 6,
    lineHeight: 1.4,
  },
  tableCell: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    fontSize: 9,
    color: COLORS.grey800,
    padding: 6,
    lineHeight: 1.4,
  },

  // ─── Progress bar ────────────────────────────────────────────────────
  progressContainer: {
    marginBottom: 8,
  },
  progressLabel: {
    fontSize: 9,
    color: COLORS.grey600,
    marginBottom: 3,
  },
  progressBarBg: {
    height: 6,
    borderRadius: 3,
    backgroundColor: COLORS.grey200,
  },

  // ─── Alert / info box ────────────────────────────────────────────────
  alertBox: {
    borderRadius: 6,
    padding: 10,
    marginBottom: 8,
    borderLeftWidth: 3,
  },
  alertText: {
    fontSize: 9,
    lineHeight: 1.5,
  },
});

// ─── Score helpers ───────────────────────────────────────────────────────────
const getScoreColor = (score: number) => {
  if (score >= 70) return COLORS.success;
  if (score >= 40) return COLORS.warning;
  return COLORS.error;
};

const getScoreBg = (score: number) => {
  if (score >= 70) return COLORS.successLight;
  if (score >= 40) return COLORS.warningLight;
  return COLORS.errorLight;
};

const getScoreLabel = (score: number) => {
  if (score >= 80) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Needs Improvement';
  return 'Critical';
};

// ─── Status detection helpers ────────────────────────────────────────────────
const STATUS_PATTERNS: [RegExp, { bg: string; color: string }][] = [
  [/^(Ready|Allowed|default-allow|explicit-allow|Not Mentioned|Yes|✅|Implemented|Active|Exists|Found|Detected|Present|Accessible|Strong)$/i, { bg: COLORS.successLight, color: COLORS.successDark }],
  [/^(Blocked|No|❌|Not Found|Missing|Absent|Denied|Not Implemented|Inaccessible|Weak)$/i, { bg: COLORS.errorLight, color: COLORS.errorDark }],
  [/^(Partial|⚠️|Limited|Warning|Needs Work|Needs Fix|In Progress|Adequate)$/i, { bg: COLORS.warningLight, color: COLORS.warningDark }],
  [/^(Critical)$/i, { bg: COLORS.errorLight, color: COLORS.errorDark }],
  [/^(High)$/i, { bg: '#fff0f0', color: '#c53030' }],
  [/^(Medium)$/i, { bg: COLORS.warningLight, color: COLORS.warningDark }],
  [/^(Low)$/i, { bg: COLORS.primaryLight, color: COLORS.primary }],
];

/**
 * Strip emojis and non-Latin symbols — @react-pdf/renderer with Inter font
 * cannot render them (they show as "L", boxes, or garbage).
 * Keep: ASCII, Latin Extended (accented chars), common punctuation.
 */
const stripEmojis = (text: string): string =>
  text
    .replace(/[\u2700-\u27BF]/g, '')       // Dingbats (✅ ❌ etc.)
    .replace(/[\u2600-\u26FF]/g, '')       // Misc symbols (⚠ etc.)
    .replace(/\uFE0F/g, '')               // Variation selector
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // Zero-width chars
    .replace(/[\u{1F000}-\u{1FFFF}]/gu, '') // All supplementary emoji blocks
    .replace(/`/g, '')                     // inline-code backticks — render as plain text (client polish)
    .trim();

/**
 * Render text with inline markdown bold (**text**) as mixed Text nodes.
 * Returns an array of React elements for use inside a <Text> wrapper.
 */
const renderInlineMarkdown = (text: string, baseStyle: Record<string, any> = {}): React.ReactNode[] => {
  const cleaned = stripEmojis(text);
  const parts = cleaned.split(/(\*\*.+?\*\*)/g);
  return parts.filter((p) => p !== '').map((part, i) => {
    const boldMatch = part.match(/^\*\*(.+?)\*\*$/);
    if (boldMatch) {
      return <Text key={i} style={[baseStyle, { fontWeight: 'bold' }]}>{boldMatch[1]}</Text>;
    }
    return <Text key={i} style={baseStyle}>{part}</Text>;
  });
};

/**
 * Detect status from cell text. Returns style + clean label for the badge.
 * Emojis like ✅/❌/⚠️ are replaced with text labels (Yes/No/Warning).
 */
/** Check if string contains a character in the given Unicode range */
const hasCharInRange = (text: string, min: number, max: number): boolean => {
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code >= min && code <= max) return true;
  }
  return false;
};

const getStatusStyle = (text: string): { bg: string; color: string; label: string } | null => {
  // Strip markdown bold/italic before matching status patterns
  const raw = text.trim().replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1');

  // Emoji-based statuses — check by char code for reliability
  // ✅ = U+2705 (0x2705 = 9989), ❌ = U+274C (0x274C = 10060), ⚠ = U+26A0 (0x26A0 = 9888)
  if (hasCharInRange(raw, 0x2705, 0x2705)) return { ...STATUS_PATTERNS[0][1], label: 'Yes' };
  if (hasCharInRange(raw, 0x274C, 0x274C)) return { ...STATUS_PATTERNS[1][1], label: 'No' };
  if (hasCharInRange(raw, 0x26A0, 0x26A0)) return { ...STATUS_PATTERNS[2][1], label: 'Warning' };

  // Text-based statuses
  const cleaned = stripEmojis(raw);
  for (const [pattern, style] of STATUS_PATTERNS) {
    if (pattern.test(cleaned)) return { ...style, label: cleaned };
  }
  return null;
};

/**
 * Internal verification-tier markers (e.g. "[verified-live]", "[inferred]") the LLM used to append
 * to Action Plan items. They are internal metadata and must NEVER surface in the client report —
 * strip them (with any leading space/tab) from the markdown before parsing. Mirrored in
 * GeoReportPreview's markdownToHtml; keep both in sync.
 */
const stripVerificationTags = (md: string): string =>
  md.replace(/[ \t]*\[\s*(?:verified[-\s]live|not[-\s]verified|inferred|rendered|archived)\s*\]/gi, '');

// ─── Markdown parsing (reused from existing) ─────────────────────────────────
const stripInlineMarkdown = (line: string): string =>
  line
    .replace(/^>\s?/, '')
    .replace(/^[-*] /, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/^### /, '')
    .replace(/^## /, '')
    .replace(/^# /, '');

type ContentBlock =
  | { type: 'text'; value: string }
  | { type: 'subheading'; value: string }
  | { type: 'bullet'; value: string; prefix?: string }
  | { type: 'table'; header: string[]; rows: string[][] };

const parseBodyBlocks = (body: string): ContentBlock[] => {
  const normalized = body.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const tableRegex = /(?:^[ \t]*\|[^\n]*\|[ \t]*\n){3,}/gm;
  const tables: { header: string[]; rows: string[][] }[] = [];
  const PLACEHOLDER = '\x00TABLE_PLACEHOLDER_';

  const withPlaceholders = normalized.replace(tableRegex, (match) => {
    const lines = match.trim().split('\n').map((l: string) => l.trim());
    const sepIdx = lines.findIndex((l: string) => /^\|[-\s|:]+\|$/.test(l));
    if (sepIdx < 0) return match;

    const parseCells = (line: string): string[] =>
      line.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c: string) => c.trim());

    const headerLines = lines.slice(0, sepIdx);
    const dataLines = lines.slice(sepIdx + 1);
    const header = headerLines.length > 0 ? parseCells(headerLines[headerLines.length - 1]) : [];
    const rows = dataLines.filter((l: string) => l.includes('|')).map((l: string) => parseCells(l));

    const idx = tables.length;
    tables.push({ header, rows });
    return `${PLACEHOLDER}${idx}\n`;
  });

  const lines = withPlaceholders.split('\n');
  const blocks: ContentBlock[] = [];

  for (const line of lines) {
    if (line.startsWith(PLACEHOLDER)) {
      const idx = parseInt(line.replace(PLACEHOLDER, ''), 10);
      if (!isNaN(idx) && tables[idx]) {
        blocks.push({ type: 'table', ...tables[idx] });
      }
      continue;
    }
    // Markdown horizontal rules (---, ***, ___) — skip so they don't render as literal "---" text.
    if (/^([-*_])\1{2,}$/.test(line.trim())) {
      continue;
    }
    // ### subheadings (kept in body since parseSections only splits on # and ##)
    if (line.trim().startsWith('### ')) {
      blocks.push({ type: 'subheading', value: line.trim().replace(/^### /, '') });
      continue;
    }
    // Numbered list items (1. , 2. , etc.)
    const numberedMatch = line.trim().match(/^(\d+)\.\s+(.+)/);
    if (numberedMatch) {
      const text = numberedMatch[2].replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1');
      blocks.push({ type: 'bullet', value: text, prefix: `${numberedMatch[1]}.` });
      continue;
    }
    // Bullet list items (- or *)
    if (/^\s*[-*] /.test(line)) {
      const text = line.trim().replace(/^[-*] /, '').replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1');
      blocks.push({ type: 'bullet', value: text });
      continue;
    }
    const trimmed = line.trim();
    if (trimmed) {
      blocks.push({ type: 'text', value: stripInlineMarkdown(trimmed) });
    }
  }

  return blocks;
};

/**
 * Split markdown into sections by # and ## headings only.
 * ### headings stay as body content (rendered as subheadings inside the card).
 */
const parseSections = (md: string): { heading: string; level: number; body: string }[] => {
  const lines = md.split('\n');
  const sections: { heading: string; level: number; body: string }[] = [];
  let current: { heading: string; level: number; body: string } | null = null;

  for (const line of lines) {
    if (line.startsWith('## ') && !line.startsWith('### ')) {
      if (current) sections.push(current);
      current = { heading: line.replace(/^## /, ''), level: 2, body: '' };
    } else if (line.startsWith('# ') && !line.startsWith('## ')) {
      if (current) sections.push(current);
      current = { heading: line.replace(/^# /, ''), level: 1, body: '' };
    } else {
      if (current) {
        current.body += (current.body ? '\n' : '') + line;
      } else {
        current = { heading: '', level: 0, body: line };
      }
    }
  }

  if (current) sections.push(current);
  return sections.filter((s) => s.heading || s.body.trim());
};

// ─── PDF sub-components ──────────────────────────────────────────────────────

/** Score display with large number and status pill */
const ScoreDisplay: React.FC<{ score: number }> = ({ score }) => {
  const color = getScoreColor(score);
  const bg = getScoreBg(score);
  const label = getScoreLabel(score);
  const barWidth = Math.min(100, Math.max(0, score)) * 2; // 200px max

  return (
    <View style={{ alignItems: 'center', marginBottom: 12 }}>
      <View style={styles.scoreRow}>
        <Text style={[styles.scoreValue, { color }]}>{score}</Text>
        <Text style={styles.scoreMax}>/100</Text>
      </View>
      {/* Score bar */}
      <View style={{ width: 200, height: 6, borderRadius: 3, backgroundColor: COLORS.grey200, marginBottom: 8 }}>
        <View style={{ width: barWidth, height: 6, borderRadius: 3, backgroundColor: color }} />
      </View>
      <View style={[styles.statusPill, { backgroundColor: bg }]}>
        <Text style={[styles.statusPillText, { color }]}>{label}</Text>
      </View>
    </View>
  );
};

/** Styled table with rounded border, status badges */
const StyledTable: React.FC<{ header: string[]; rows: string[][] }> = ({ header, rows }) => {
  // Render one data row. wrap={false} keeps a single row intact: without it a row straddling a page
  // break splits, orphaning a cell (e.g. a crawler's status badge rendered on the previous page
  // while its name/platform move to the next). ri is the row's ORIGINAL index (for isLast/isEven).
  const renderRow = (row: string[], ri: number) => {
    const isLast = ri === rows.length - 1;
    const isEven = ri % 2 === 1;
    return (
      <View
        key={ri}
        wrap={false}
        style={[
          isLast ? styles.tableRowLast : styles.tableRow,
          isEven ? styles.tableRowEven : {},
        ]}
      >
        {row.map((cell, ci) => {
          const statusStyle = getStatusStyle(cell);
          if (statusStyle) {
            return (
              <View key={ci} style={[styles.tableCell, { flexDirection: 'row', alignItems: 'center' }]}>
                <View style={{
                  backgroundColor: statusStyle.bg,
                  paddingHorizontal: 6,
                  paddingVertical: 2,
                  borderRadius: 8,
                }}>
                  <Text style={{ fontSize: 8, fontWeight: 'bold', color: statusStyle.color }}>
                    {statusStyle.label}
                  </Text>
                </View>
              </View>
            );
          }
          return (
            <Text key={ci} style={styles.tableCell}>
              {renderInlineMarkdown(cell, { fontSize: 9, color: COLORS.grey800 })}
            </Text>
          );
        })}
      </View>
    );
  };

  const headerRow =
    header.length > 0 ? (
      <View style={styles.tableHeaderRow} wrap={false}>
        {header.map((cell, ci) => (
          <Text key={ci} style={styles.tableHeaderCell}>
            {cell}
          </Text>
        ))}
      </View>
    ) : null;

  // Glue the header to the first data row (wrap={false}) so the header is never left stranded at the
  // bottom of a page with its rows pushed to the next one. Remaining rows flow and can break freely.
  const gluedHeader = headerRow !== null && rows.length > 0;

  return (
    // No wrap={false} on the container: a long table (e.g. §7's 26 schema rows) taller than a page
    // would otherwise overflow/clip or leave a large gap. Let it break across pages between rows.
    <View style={styles.tableContainer}>
      {gluedHeader ? (
        <View wrap={false}>
          {headerRow}
          {renderRow(rows[0], 0)}
        </View>
      ) : (
        headerRow
      )}
      {(gluedHeader ? rows.slice(1) : rows).map((row, i) =>
        renderRow(row, gluedHeader ? i + 1 : i)
      )}
    </View>
  );
};

/** Bullet list with colored dots or numbered prefixes, with sub-item indentation */
const StyledBulletList: React.FC<{ items: { text: string; prefix?: string }[] }> = ({ items }) => {
  // Detect nesting: plain bullets after a numbered item are sub-items
  let inNumbered = false;

  return (
    <View style={styles.bulletContainer}>
      {items.map((item, i) => {
        const isNumbered = !!item.prefix;
        if (isNumbered) inNumbered = true;
        const isSubItem = !isNumbered && inNumbered;

        return (
          <View key={i} style={[styles.bulletRow, isSubItem ? { paddingLeft: 18 } : {}, isNumbered && i > 0 ? { marginTop: 8 } : {}]} wrap={false}>
            {isNumbered ? (
              <Text style={[styles.bulletNumbered, { color: COLORS.primary }]}>
                {item.prefix}
              </Text>
            ) : (
              <Text style={[styles.bulletDot, { color: isSubItem ? COLORS.grey400 : COLORS.primary }]}>
                {'\u2022'}
              </Text>
            )}
            <Text style={[styles.bulletText, isNumbered ? { fontWeight: 'bold' } : {}]}>
              {item.text}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

/** Progress bar for percentage values */
const ProgressBar: React.FC<{ label: string; value: number; color?: string }> = ({
  label,
  value,
  color,
}) => {
  const barColor = color || getScoreColor(value);
  const barWidthPercent = `${Math.min(100, Math.max(0, value))}%`;

  return (
    <View style={styles.progressContainer}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
        <Text style={styles.progressLabel}>{label}</Text>
        <Text style={[styles.progressLabel, { fontWeight: 'bold' }]}>{value}%</Text>
      </View>
      <View style={styles.progressBarBg}>
        <View style={{ width: barWidthPercent, height: 6, borderRadius: 3, backgroundColor: barColor }} />
      </View>
    </View>
  );
};

/** Alert/info box with colored left border */
const AlertBox: React.FC<{
  text: string;
  variant: 'success' | 'warning' | 'error' | 'info';
}> = ({ text, variant }) => {
  const variantMap = {
    success: { bg: COLORS.successLight, border: COLORS.success, color: COLORS.successDark },
    warning: { bg: COLORS.warningLight, border: COLORS.warning, color: COLORS.warningDark },
    error: { bg: COLORS.errorLight, border: COLORS.error, color: COLORS.errorDark },
    info: { bg: COLORS.primaryLight, border: COLORS.primary, color: COLORS.primary },
  };
  const v = variantMap[variant];

  return (
    <View style={[styles.alertBox, { backgroundColor: v.bg, borderLeftColor: v.border }]} wrap={false}>
      <Text style={[styles.alertText, { color: v.color }]}>{text}</Text>
    </View>
  );
};

// ─── Detect alert-like text patterns ─────────────────────────────────────────
const detectAlertVariant = (text: string): 'success' | 'warning' | 'error' | 'info' | null => {
  const lower = text.toLowerCase();
  if (/^(⚠️|warning:|caution:|note:)/.test(lower) || /\brisk\b/.test(lower)) return 'warning';
  if (/^(❌|error:|critical:|urgent:)/.test(lower)) return 'error';
  if (/^(✅|success:|implemented:)/.test(lower)) return 'success';
  if (/^(ℹ️|info:|tip:|recommendation:)/.test(lower)) return 'info';
  return null;
};

// ─── Detect score/percentage patterns in text for progress bars ──────────────
const detectProgress = (text: string): { label: string; value: number } | null => {
  const match = text.match(/^(.+?):\s*(\d{1,3})(?:%|\s*\/\s*100)\s*$/);
  if (match) {
    const val = parseInt(match[2], 10);
    if (val >= 0 && val <= 100) {
      return { label: match[1].trim(), value: val };
    }
  }
  return null;
};

/**
 * Wraps the section heading + first content element in a single wrap={false} View,
 * so the title is never orphaned at the bottom of a page.
 * Remaining content flows normally and can break across pages.
 */
const SectionContentWithTitle: React.FC<{ heading?: string; blocks: ContentBlock[] }> = ({ heading, blocks }) => {
  const allElements = renderBlocks(blocks);

  if (!heading) {
    return <>{allElements}</>;
  }

  const title = <Text style={styles.cardTitle}>{heading}</Text>;

  if (allElements.length === 0) {
    return title;
  }

  // Title + first content element stay together
  const first = allElements[0];
  const rest = allElements.slice(1);

  return (
    <>
      <View wrap={false}>
        {title}
        {first}
      </View>
      {rest}
    </>
  );
};

// ─── Section content renderer ────────────────────────────────────────────────
const renderBlocks = (blocks: ContentBlock[]): React.ReactNode[] => {
  const rendered: React.ReactNode[] = [];
  let bulletBuffer: { text: string; prefix?: string }[] = [];
  let key = 0;

  const flushBullets = () => {
    if (bulletBuffer.length > 0) {
      rendered.push(<StyledBulletList key={`bl-${key++}`} items={bulletBuffer} />);
      bulletBuffer = [];
    }
  };

  for (const block of blocks) {
    if (block.type === 'bullet') {
      bulletBuffer.push({ text: stripEmojis(block.value), prefix: block.prefix });
      continue;
    }

    flushBullets();

    if (block.type === 'table') {
      rendered.push(
        <StyledTable key={`tbl-${key++}`} header={block.header} rows={block.rows} />
      );
    } else if (block.type === 'subheading') {
      const cleanValue = stripEmojis(block.value);
      if (!cleanValue) continue;
      rendered.push(
        <Text key={`sh-${key++}`} style={styles.subheading}>{cleanValue}</Text>
      );
    } else {
      const cleanValue = stripEmojis(block.value);
      if (!cleanValue) continue;

      // Check for alert-like content
      const alertVariant = detectAlertVariant(block.value);
      if (alertVariant) {
        rendered.push(
          <AlertBox key={`alert-${key++}`} text={cleanValue} variant={alertVariant} />
        );
        continue;
      }

      // Check for progress/score pattern
      const progress = detectProgress(cleanValue);
      if (progress) {
        rendered.push(
          <ProgressBar key={`prog-${key++}`} label={progress.label} value={progress.value} />
        );
        continue;
      }

      rendered.push(
        <Text key={`txt-${key++}`} style={styles.bodyText}>{cleanValue}</Text>
      );
    }
  }

  flushBullets();
  return rendered;
};

// ─── Main PDF Document ───────────────────────────────────────────────────────
interface GeoReportPdfDocumentProps {
  report: GeoReportResponse;
}

const GeoReportPdfDocument: React.FC<GeoReportPdfDocumentProps> = ({ report }) => {
  const sections = report.reportMarkdown
    ? parseSections(stripVerificationTags(report.reportMarkdown))
    : [];
  const completedDate = report.completedAt
    ? format(new Date(report.completedAt), 'dd MMM yyyy')
    : format(new Date(report.createdAt), 'dd MMM yyyy');
  const score = report.geoScore ?? 0;
  const cleanUrl = report.url.replace(/^https?:\/\//, '');

  // Split sections: first h1 section is title (skip it — we render cover), rest are cards
  const contentSections = sections.filter((s) => s.level !== 1 || s.body.trim());

  return (
    <Document>
      {/* Page 1: Cover with branded background, centered logo */}
      <Page size="A4" style={styles.coverPageStyle}>
        <View style={styles.coverBgWrapper} fixed>
          <Image src={chatbeatPdfBg} style={styles.coverBgImage} />
        </View>

        <View style={styles.coverContent}>
          <Image src={chatbeatLogoFull} style={styles.coverLogo} />

          <View style={styles.coverBlock}>
            <Text style={styles.coverTitle}>GEO Audit</Text>
            <Text style={styles.coverUrl}>{cleanUrl}</Text>
            <Text style={styles.coverDate}>Generated {completedDate}</Text>

            {report.geoScore !== null && <ScoreDisplay score={score} />}
          </View>
        </View>

        <Text
          style={styles.coverFooter}
          fixed
          render={({ pageNumber, totalPages }) => `${pageNumber}/${totalPages}`}
        />
      </Page>

      {/* Content pages: white background */}
      <Page size="A4" style={styles.contentPage}>
        <View style={styles.contentHeader} fixed>
          <Image src={chatbeatLogoFull} style={styles.contentHeaderLogo} />
        </View>

        {contentSections.map((section, idx) => {
          const blocks = parseBodyBlocks(section.body);

          if (blocks.length === 0 && !section.heading) return null;
          if (section.level === 1 && blocks.length === 0) return null;

          return (
            <View key={idx} style={styles.section}>
              <SectionContentWithTitle heading={section.heading} blocks={blocks} />
            </View>
          );
        })}

        <Text
          style={styles.contentFooter}
          fixed
          render={({ pageNumber, totalPages }) => `${pageNumber}/${totalPages}`}
        />
      </Page>
    </Document>
  );
};

export { GeoReportPdfDocument };
export default GeoReportPdfDocument;
